/**
 * Builds structured search filter trees for the oncology submission table.
 */

/** Single predicate condition node for BigQuery structured filters. */
export type FilterCondition = {
  type: "condition";
  field: string;
  operator: string;
  value?: string | boolean | Array<string | boolean>;
};

/** AND/OR group node for BigQuery structured filters. */
export type FilterGroup = {
  type: "group";
  operator: "and" | "or";
  children: (FilterCondition | FilterGroup)[];
};

/**
 * Builds the seq condition, supporting a single term or a comma separated MRN list.
 *
 * @input trimmed - Trimmed search text from the table search input
 * @return FilterCondition: is_any_of condition for MRN lists, contains otherwise
 */
const buildSeqCondition = (trimmed: string): FilterCondition => {
  const seqValues = trimmed
    .split(",")
    .map((seq) => seq.trim())
    .filter((seq) => seq.length > 0);

  if (seqValues.length > 1) {
    return {
      type: "condition",
      field: "seq",
      operator: "is_any_of",
      value: seqValues,
    };
  }

  return {
    type: "condition",
    field: "seq",
    operator: "contains",
    value: trimmed,
  };
};

/**
 * Builds the OR search filter tree matching seq, patient_name, or first/last name.
 *
 * @input searchTerm - Raw search text from the table search input
 * @return FilterGroup: OR group with seq, patient_name, and name AND conditions
 */
export const buildSearchFilterTree = (searchTerm: string): FilterGroup => {
  const trimmed = searchTerm.trim();
  const parts = trimmed.split(/\s+/);
  const firstName = parts[0] ?? trimmed;
  const lastName = parts.slice(1).join(" ") || firstName;

  return {
    type: "group",
    operator: "or",
    children: [
      buildSeqCondition(trimmed),
      {
        type: "condition",
        field: "patient_name",
        operator: "contains",
        value: trimmed,
      },
      {
        type: "group",
        operator: "and",
        children: [
          {
            type: "condition",
            field: "patient_first_name",
            operator: "contains",
            value: firstName,
          },
          {
            type: "condition",
            field: "patient_last_name",
            operator: "contains",
            value: lastName,
          },
        ],
      },
    ],
  };
};

/**
 * Converts legacy flat sidebar filters into predicate condition nodes.
 *
 * @input apiFilters - Flat filter map from processFilterData
 * @return FilterCondition[]: Predicate conditions for sidebar filters
 */
export const convertLegacyFiltersToConditions = (
  apiFilters: Record<string, string[] | null>,
): FilterCondition[] => {
  const conditions: FilterCondition[] = [];

  Object.entries(apiFilters).forEach(([field, values]) => {
    if (!values || values.length === 0) {
      return;
    }

    if (field === "dumped_at" && values.length >= 2) {
      conditions.push({
        type: "condition",
        field: "dumped_at",
        operator: ">=",
        value: values[0],
      });
      conditions.push({
        type: "condition",
        field: "dumped_at",
        operator: "<=",
        value: values[1],
      });
      return;
    }

    conditions.push({
      type: "condition",
      field,
      operator: "is_any_of",
      value: values,
    });
  });

  return conditions;
};

/**
 * Merges the search filter tree with any active sidebar filters.
 *
 * @input searchTerm - Raw search text from the table search input
 * @input apiFilters - Flat filter map from processFilterData (may include sidebar filters)
 * @return FilterGroup: Combined AND tree, or the search-only tree when no sidebar filters exist
 */
export const mergeSearchFiltersWithSidebarFilters = (
  searchTerm: string,
  apiFilters: Record<string, string[] | null>,
): FilterGroup => {
  const searchTree = buildSearchFilterTree(searchTerm);
  const sidebarFilters = { ...apiFilters };
  delete sidebarFilters.seq;

  const sidebarConditions = convertLegacyFiltersToConditions(sidebarFilters);

  if (sidebarConditions.length === 0) {
    return searchTree;
  }

  return {
    type: "group",
    operator: "and",
    children: [...sidebarConditions, searchTree],
  };
};
